import React from 'react';
import './TokenMuster.css';
import {joinClassNames} from '../../gameUi/joinClassNames';

/**
 * Места жетонов в ряду: номер, сколько зазоров между группами перед жетоном, а при
 * раскладке в несколько рядов — ряд и номер в нем. По ним встает пятно света
 *
 * @param {Array<{id: string, isGroupStart?: boolean}>} tokens - жетоны по порядку
 * @param {number} [columns] - ширина рядов в жетонах: каждая группа — свой ряд, короткий
 *   по центру (место может быть дробным); без него все в один ряд
 * @returns {Object} {[id]: {slot, group, row, isGroupStart}}
 */
export const getTokenRowSlots = (tokens, columns) => {
  const groupSizes = [];
  const tokenGroups = tokens.map(({isGroupStart}, index) => {
    if (index === 0 || isGroupStart) {
      groupSizes.push(0);
    }
    groupSizes[groupSizes.length - 1] += 1;
    return groupSizes.length - 1;
  });

  const placedInGroup = [];
  return tokens.reduce((slots, {id, isGroupStart}, index) => {
    const group = tokenGroups[index];
    const placeInGroup = placedInGroup[group] ?? 0;
    placedInGroup[group] = placeInGroup + 1;
    return {
      ...slots,
      [id]: {
        slot: columns ? placeInGroup + (columns - groupSizes[group]) / 2 : index,
        row: columns ? group : 0,
        group: columns ? 0 : group,
        isGroupStart: Boolean(isGroupStart),
      },
    };
  }, {});
};

/** Сколько зазоров между группами в ряду — от него зависит ширина жетона на телефоне */
export const countTokenGroupGaps = (tokens) => tokens.filter(({isGroupStart}) => isGroupStart).length;

/**
 * Пятно света цвета игрока позади ряда: перетекает к показанному жетону. Гаснет на
 * прежнем месте: координаты остаются от последнего жетона, и при новом показе оно
 * проявляется, а не летит из начала ряда
 *
 * @param {{slot: number, group: number, row: number}} [spot] - где встать; без него гаснет
 */
export const TokenSpotlight = ({spot}) => (
  <span
    className={joinClassNames('token-muster_spotlight', spot && 'token-muster_spotlight_active')}
    style={spot ? {'--spot-slot': spot.slot, '--spot-group': spot.group, '--spot-row': spot.row} : undefined}
    aria-hidden={true}
  />
);

/**
 * Подпись над жетоном: название показанного строения в ряду без описания. Ездит вместе с
 * пятном света
 *
 * @param {{slot: number, group: number}} spot - над каким жетоном
 * @param {string} text
 */
export const TokenCaption = ({spot, text}) => (
  <span
    className={'token-muster_caption'}
    style={spot ? {'--spot-slot': spot.slot, '--spot-group': spot.group} : undefined}
    aria-hidden={true}
  >
    {text}
  </span>
);

/** Блик по камню жетона при наведении */
export const TokenGlint = () => <span className={'token-muster_glint'} aria-hidden={true}/>;

/**
 * Цена — плашка на нижней грани жетона. Лежит в самой кнопке: поднимается и
 * нажимается вместе с жетоном
 *
 * @param {number} [price] - без цены плашка показывает только children
 * @param {React.ReactNode} [children] - значки в строку: скидка, свободное действие
 */
export const TokenPrice = ({price, children}) => (
  <span className={'token-muster_price'}>
    {price !== undefined && <>
      <img src={'/img/icons/coins.png'} alt={'Цена'}/>
      {price}
    </>}
    {children}
  </span>
);
